/* eslint-disable no-prototype-builtins */
import { useEffect, useState } from 'react'
import { EmojiClickData } from 'emoji-picker-react'
import { isEmpty as _isEmpty } from 'lodash'

import { IFiles } from 'types/file.types'
import { useAppSelector } from 'app/hooks'
import { getInputValueByfullname, geValueByfullname } from 'utils/helpers/messageHelper'
import { FixMeLater } from 'types'
import { useDispatch } from 'react-redux'
import { newsFeedActions, setCommentData } from 'app/slices/news-feed.slice'
import { useCreatePostMutation, useSendCommentMutation } from 'app/services/post.service'
import axiosApi from 'app/axiosApi'
import { getParseMentionedUsers } from './useMessageInput'

const isEmpty = (str: string) => !str.trim().length

const inputDataValidation = (text = '', files?: IFiles, postId?: string, header = '') => {
  if (postId) return !isEmpty(text)
  // Yeni post: hem başlık hem içerik zorunlu
  return !isEmpty(header) && !isEmpty(text)
}

const useCreatePostInput = ({ inputData, postId }: { inputData: FixMeLater; postId?: string }) => {
  const [isValid, setValid] = useState(false)
  const [createPost] = useCreatePostMutation()
  const [createComment] = useSendCommentMutation()

  const myId = useAppSelector((state) => state.auth.id)
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const [useInputData, setInputData] = useState<FixMeLater>(inputData) // using as content.

  const dispatch = useDispatch()

  const onCancelFile = (index: number) => {
    dispatch(newsFeedActions.cancelFile(index))
    setInputData((prevState: FixMeLater) => ({
      ...prevState,
      files: prevState.files.filter((item: FixMeLater, itemIndex: number) => itemIndex !== index),
    }))
  }

  const onChangeInputData = async (text: string) => {
    const mentionedUsers = getParseMentionedUsers(text)
    if (postId) {
      setInputData((prevState: FixMeLater) => ({
        ...prevState,
        text,
        value: text,
        mentionedUsers,
        postId,
      }))
      return dispatch(
        newsFeedActions.changeCommentData({ text, value: text, mentionedUsers, postId }),
      )
    }
    dispatch(newsFeedActions.changeInputData({ text, value: text, mentionedUsers }))

    setInputData((prevState: FixMeLater) => ({
      ...prevState,
      text,
      value: text,
      mentionedUsers,
      postId,
    }))
  }

  const onChangeSelectedTeam = async (teamId: string) => {
    setInputData((prevState: FixMeLater) => ({ ...prevState, teamId }))
  }

  const onChangeHeaderInputData = async (header: string) => {
    // dispatch(newsFeedActions.changeInputData({ header }))
    setInputData((prevState: FixMeLater) => ({ ...prevState, header }))
  }

  const onChangeToggleComment = (commentToggle: FixMeLater) => {
    // dispatch(newsFeedActions.changeInputData({commentToggle}))
    setInputData((prevState: FixMeLater) => ({ ...prevState, commentToggle }))
  }

  const onChangeInputFileData = (files: FixMeLater) => {
    dispatch(newsFeedActions.changeInputFileData(files))
    setInputData((prevState: FixMeLater) => ({
      ...prevState,
      files: [...prevState.files, ...files],
    }))
  }

  const onSelectEmoji = (emojiParams: EmojiClickData) => {
    if (postId) {
      dispatch(newsFeedActions.selectEmojiForComment({ ...emojiParams, postId }))
      onChangeInputData(inputData.text + emojiParams.emoji)
      return
    }

    dispatch(newsFeedActions.selectEmoji(emojiParams))
    onChangeInputData(inputData.text + emojiParams.emoji)
  }

  const onNewPost = async () => {
    const sentAt = new Date().toISOString()

    // Remove generateObjectId and uuid usage
    // Remove uuid from params and all related usages
    const {
      mentionedUsers,
      text,
      files = [],
      header,
      commentToggle = false,
    } = getInputValueByfullname(useInputData)
    const value = geValueByfullname(text, mentionedUsers)
    const teamId = useInputData?.teamId ?? 'general'
    const trimmedText = text.trim()

    dispatch(
      newsFeedActions.changeInputData({
        header: header,
        text: text,
        value: text,
        mentionedUsers: mentionedUsers,
        commentToggle: commentToggle ?? false,
        teamId,
        files: files,
      }),
    )
    const params: {
      type: Array<string>
      sentAt: string
      commentToggle: boolean
      teamId: string
      content: {
        header: string
        mentionedUsers: Array<string>
        text: string

        value: string
        files: Array<File>
      }
    } = {
      type: [],
      sentAt,
      commentToggle,
      teamId,
      content: { header, mentionedUsers, text: trimmedText, value, files },
    }

    if (!isEmpty(text)) {
      params.type.push('text')
    }
    if (!_isEmpty(files)) {
      params.type.push('files')
    }

    try {
      dispatch(newsFeedActions.resetInputData())
      for (const [, item] of teamId.entries()) {
        dispatch(
          newsFeedActions.createPost({
            status: 'pending',
            ...params,
            ownerId: myId,
            teamId: item._id,
          }),
        )
      }

      if (params.content.files.length > 0) {
        dispatch(newsFeedActions.postLoading(true))

        try {
          const result = await axiosApi.uploadFile(
            `files/uploads?from=post&method=create&teams=${encodeURIComponent(
              JSON.stringify(teamId.map((item: any) => item?._id)),
            )}`,
            params.content.files,
            'file',
            { params: params },
            undefined,
            accessToken,
          )
        } catch (error) {
          console.error('File upload error:', error)
        }
      } else {
        const { data }: FixMeLater = await createPost({ ...params, teams: teamId })

        if (data.success) {
          dispatch(newsFeedActions.postLoading(false))
          for (const [, item] of data.data.entries()) {
            dispatch(newsFeedActions.createPost(item))
          }
        }
      }
    } catch (e: unknown) {
      console.error('Trouble creating post', e)
      console.error('Error details:', {
        message: e instanceof Error ? e.message : 'Unknown error',
        stack: e instanceof Error ? e.stack : undefined,
        response:
          e && typeof e === 'object' && 'response' in e
            ? (e as { response: unknown }).response
            : undefined,
      })
      dispatch(newsFeedActions.deletePost({ sentAt }))
      dispatch(newsFeedActions.postLoading(false))
    }
  }

  const onNewCommentForPost = async () => {
    const sentAt = new Date().toISOString()
    const { mentionedUsers, text } = getInputValueByfullname(useInputData)
    const value = geValueByfullname(text, mentionedUsers)
    const trimmedText = text.trim()
    const params: {
      type: Array<string>
      sentAt: string
      postId: string | undefined
      content: { mentionedUsers: Array<string>; text: string; value: string }
    } = {
      type: [],
      sentAt,
      postId: postId,
      content: { mentionedUsers, text: trimmedText, value },
    }

    if (!isEmpty(text)) {
      params.type.push('text')
    }

    try {
      dispatch(newsFeedActions.resetCommentData({ postId }))
      const comment = (await createComment(params)) as FixMeLater
      if (comment?.data?.success) {
        dispatch(newsFeedActions.commentLoading(false))
        setInputData((prevState: FixMeLater) => ({
          ...prevState,
          text: '',
          value: '',
        }))
        return setCommentData(comment.data.data)
      }
    } catch (e) {
      console.error('Error creating comment', e)
    }
  }

  useEffect(() => {
    if (postId) {
      const valid = inputDataValidation(
        useInputData?.text || '',
        [],
        postId,
        useInputData?.header ?? '',
      )

      return setValid(valid)
    }
    const contentValid = inputDataValidation(
      useInputData?.text,
      useInputData?.files,
      undefined,
      useInputData?.header ?? '',
    )
    const hasTeamSelected =
      Array.isArray(useInputData?.teamId) && useInputData.teamId.length > 0

    setValid(contentValid && hasTeamSelected)
  }, [useInputData])

  return {
    isValid,
    onCancelFile,
    onNewPost,
    onSelectEmoji,
    onChangeInputData,
    onNewCommentForPost,
    onChangeInputFileData,
    onChangeHeaderInputData,
    onChangeToggleComment,
    onChangeSelectedTeam,
  }
}

export default useCreatePostInput
